import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  afterNextRender,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotesWithMentionsDto } from '../../../models/notes.models';
import {
  EmployeeAssignmentDto,
  MatterAssignmentDto,
} from '../../../models/request.models';
import { AssignmentService } from '../../../services/assignment.service';
import { AuthService } from '../../../services/auth.service';
import { FloatingDialogService } from '../../../services/floating-dialog.service';
import { resolveMentionLinks } from '../../../utils/mention.utils';
import { formatUserEditHistoryName } from '../../../utils/user-name.utils';
import { MatterCellComponent } from '../matter-cell/matter-cell.component';

export type AssignmentNotesPanelMode = 'matter' | 'resource';

/** The assignment DTOs either host already holds: one matter's or one resource's. */
export type AssignmentNotesSource = MatterAssignmentDto | EmployeeAssignmentDto;

/**
 * Which notes the panel is currently listing:
 * - `mine`     the viewer's own note(s)
 * - `resource` other resources' own notes (requires Assignments.ResourceNotes.Read)
 * - `manager`  manager-authored notes
 */
export type AssignmentNotesView = 'mine' | 'resource' | 'manager';

/**
 * Help text for the resource and manager views, or null where none is
 * warranted.
 *
 * Each view describes only its own note type — the contrast with the other type
 * is left implicit, since the reader is looking at one tab at a time and the
 * other tab carries its own explanation. "My Note" is deliberately excluded: it
 * is the viewer's own note, which needs no explanation.
 *
 * Module-level so the strings are built once for the application rather than
 * re-concatenated on every read of viewHelpTooltip.
 */
const VIEW_HELP_TOOLTIPS: Record<AssignmentNotesView, string | null> = {
  mine: null,
  resource:
    'Resource notes are written by the assigned resources themselves — ' +
    'typically their own status, availability or context on the assignment.',
  manager:
    'Manager notes are written by staffing managers about the assignment ' +
    'and the resource staffed on it.',
};

/**
 * One note in the panel — one row per note, so each view keeps a strict 1:1
 * mapping between sidebar entries and detail sections (the scroll-spy depends
 * on it).
 *
 * Locked is a manager-note-only state: it represents a note that exists but is
 * hidden by resource visibility settings (hasManagerNote is true while
 * managerNotesInfo arrives null from the API). A resource note withholds its
 * existence flag along with its content, so it is never locked — if it is
 * listed, it is readable.
 */
interface NoteRow {
  // Matter-mode fields (the resource the note belongs to)
  fullName?: string;
  title?: string;
  classYear?: string;
  seniority?: string;

  /**
   * Primary/Coverage. Shown beside the name in matter mode, where one resource
   * can hold several assignments on the same matter and the name alone would
   * not tell two of their notes apart.
   */
  assignmentTypeCategoryName?: string;

  // Resource-mode fields (the matter the note belongs to)
  clientMatterNumber?: string;
  clientName?: string;
  matterTitle?: string;

  /**
   * The assignment the note belongs to. A stable key across re-feeds, so the
   * selection can follow the note when entries are added or removed around it,
   * and the key both lists are tracked by, so it must be present and unique.
   */
  assignmentId: number;

  locked: boolean;
  noteHtml: string;
  lastUpdatedName?: string;
  lastUpdatedTimestamp?: string;

  /**
   * The employee whose note this is. Stored rather than resolved to a boolean at
   * build time so ownership can be re-evaluated whenever the current user
   * changes — see applyOwnershipSplit.
   */
  employeeId?: string;

  /**
   * True when this is the viewer's own resource note; drives the "My Note"
   * marker. Derived from employeeId by applyOwnershipSplit, never assigned at
   * row-build time.
   */
  isOwnNote?: boolean;
}

/**
 * Lists all assignment notes for a matter or a resource in a master-detail
 * layout: a sidebar of resources/matters and a scrollable panel of full notes.
 *
 * It has two hosts, told apart by floatingDialogId (see isDialogMode):
 * - Inline, as the Assignment Notes card on Resource Details and Matter
 *   Details. The page passes in the assignments it has already fetched, so the
 *   card costs no second request and follows the page's own refreshes. The
 *   card's header offers a pop-out into a floating dialog.
 * - In a floating dialog (FloatingDialogService provides drag, minimize and
 *   resize). Here it fetches assignments itself rather than receiving a
 *   snapshot, because floating dialogs outlive the page they were opened from.
 */
@Component({
  selector: 'app-assignment-notes-panel',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatterCellComponent,
  ],
  templateUrl: './assignment-notes-panel.component.html',
  styleUrls: ['./assignment-notes-panel.component.scss'],
})
export class AssignmentNotesPanelComponent implements OnInit, OnDestroy {
  /** Which entity the notes belong to; drives the fetch and the labels. */
  @Input() mode: AssignmentNotesPanelMode = 'matter';

  /** Client-matter number (matter mode). */
  @Input() clientMatterNumber?: string;

  /** Employee id (resource mode). */
  @Input() employeeId?: string;

  /**
   * Auto-injected by FloatingDialogService. Its presence is the signal that
   * the panel is in dialog mode (see isDialogMode).
   */
  @Input() floatingDialogId?: string;

  /**
   * The resource's full name or the matter name. Used only to title the
   * pop-out dialog; the identifier stands in when it is absent.
   */
  readonly entityName = input<string | null>(null);

  /**
   * The assignments to list, supplied by an inline host. null means the host
   * is loading — on first load or on a reload after an assignment is added or
   * removed — and puts the panel in its loading state; [] means loaded with
   * nothing to show. Ignored in dialog mode, which fetches its own.
   */
  readonly assignments = input<AssignmentNotesSource[] | null>(null);

  /**
   * Set by an inline host whose fetch failed, so the panel shows its error
   * state instead of reading the host's empty fallback list as "no notes".
   * Ignored in dialog mode.
   */
  readonly loadFailed = input(false);

  private assignmentService = inject(AssignmentService);
  private authService = inject(AuthService);
  private floatingDialogService = inject(FloatingDialogService);
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);

  get isDialogMode(): boolean {
    return !!this.floatingDialogId;
  }

  constructor() {
    // An inline host re-supplies the list whenever it changes: after its own
    // refetch (an assignment added or removed) and after a note edit, which
    // the host applies to a new copy of the list. Rebuilding on each new list
    // is what keeps the card in step with the page without fetching.
    effect(() => {
      const assignments = this.assignments();
      const loadFailed = this.loadFailed();
      untracked(() => {
        if (this.isDialogMode) return;
        if (assignments === null) {
          // Back to loading on a reload too: the previous rows may include an
          // assignment that has just been removed.
          this.isLoading = true;
          this.error = null;
        } else if (loadFailed) {
          this.setError();
        } else {
          this.setRows(assignments);
        }
      });
    });
  }

  /**
   * One list per view. Each is self-contained — the active view's list is what
   * the master-detail renders, so every view shares the same markup and only
   * its contents differ.
   */
  myNoteRows: NoteRow[] = [];
  resourceNoteRows: NoteRow[] = [];
  managerNoteRows: NoteRow[] = [];

  /**
   * Every resource note, unsplit. myNoteRows/resourceNoteRows are views onto
   * this list, re-derived whenever the current user changes.
   */
  private allResourceNoteRows: NoteRow[] = [];

  // Starts on 'mine', the top of the default priority: the first feed keeps the
  // current view when it has notes, which then is the default (see defaultView).
  activeView: AssignmentNotesView = 'mine';

  /**
   * Set once the viewer picks a view themselves. After that the default-view
   * priority no longer applies: a late-arriving user record must not yank the
   * reader off a tab they clicked.
   */
  private viewChosenByUser = false;

  isLoading = true;
  error: string | null = null;

  selectedIndex = 0;

  /** The active view's notes. Everything below (scroll-spy included) reads this. */
  get rows(): NoteRow[] {
    return this.rowsFor(this.activeView);
  }

  private rowsFor(view: AssignmentNotesView): NoteRow[] {
    switch (view) {
      case 'mine':
        return this.myNoteRows;
      case 'resource':
        return this.resourceNoteRows;
      default:
        return this.managerNoteRows;
    }
  }

  /** True once loading finished and no view has anything to show. */
  get hasNoNotesAtAll(): boolean {
    return (
      this.myNoteRows.length === 0 &&
      this.resourceNoteRows.length === 0 &&
      this.managerNoteRows.length === 0
    );
  }

  /**
   * Only views with notes are offered. An empty view has nothing to show and no
   * way to add anything — this panel is read-only — so listing it would be
   * clutter, and "My Note" would sit empty on every matter the viewer is not
   * assigned to.
   *
   * A plain field, not a getter: the list only changes when the rows do, but the
   * template reads it twice per change-detection pass, and every pass would
   * rebuild the array, its objects and its interpolated labels. The pane's
   * scroll listener runs outside the zone and checks the template only when the
   * selection changes, but that is still several passes per scroll through the
   * notes. Rebuilt by applyOwnershipSplit instead.
   */
  availableViews: { view: AssignmentNotesView; label: string }[] = [];

  private buildAvailableViews(): {
    view: AssignmentNotesView;
    label: string;
  }[] {
    const views: { view: AssignmentNotesView; label: string }[] = [];
    if (this.myNoteRows.length > 0) {
      // Singular with no count in the common case (one assignment, one note).
      // Neither mode guarantees that, though: resource mode lists one note per
      // matter, and matter mode one per assignment — and a resource can hold
      // several assignments on one matter (Primary plus Coverage), so the same
      // person can appear more than once. Pluralise on the count either way.
      views.push({
        view: 'mine',
        label:
          this.myNoteRows.length > 1
            ? `My Notes (${this.myNoteRows.length})`
            : 'My Note',
      });
    }
    if (this.resourceNoteRows.length > 0) {
      views.push({
        view: 'resource',
        label: `Resource Notes (${this.resourceNoteRows.length})`,
      });
    }
    if (this.managerNoteRows.length > 0) {
      views.push({
        view: 'manager',
        label: `Manager Notes (${this.managerNoteRows.length})`,
      });
    }
    return views;
  }

  /**
   * A one-option switch would be a control that does nothing. Hidden while
   * loading or in error too: the counts would describe rows that are no longer
   * on screen, and a pick made from them would be a choice on stale data.
   */
  get showViewToggle(): boolean {
    return !this.isLoading && !this.error && this.availableViews.length > 1;
  }

  /**
   * Switches view and returns to the top. The note sections are rebuilt for the
   * new list, so scroll the pane directly rather than scrolling a section that
   * no longer exists; the spy is suppressed for the step, as in selectNote.
   *
   * The sidebar is reset alongside it: the <aside> element survives the switch
   * (same @if branch), so its scroll position would
   * otherwise carry over and leave the newly selected first row off-screen.
   * The wheel accumulator is part of that state too — a partial step left over
   * from the previous view would offset the first step taken in this one.
   */
  setActiveView(view: AssignmentNotesView): void {
    // Nothing to switch between while loading or in error (see showViewToggle).
    if (this.isLoading || this.error) return;
    if (view === this.activeView) return;
    this.viewChosenByUser = true;
    this.activeView = view;
    this.selectedIndex = 0;
    this.resetPanesToTop();
  }

  /**
   * Returns both panes to the top for a newly shown view, with the spy
   * suppressed for the step. Shared by every path that switches the view —
   * the reader's click and a refresh that moves them off an emptied view — so
   * the new view never inherits the old one's scroll positions or a partial
   * wheel step.
   */
  private resetPanesToTop(): void {
    this.sidebarWheelAccum = 0;
    this.isProgrammaticScroll = true;
    this.restartScrollSettleTimer();
    const pane = this.detailPane?.nativeElement;
    if (pane) pane.scrollTop = 0;
    const sidebar = this.sidebar?.nativeElement;
    if (sidebar) sidebar.scrollTop = 0;
  }

  /** Suppresses the scroll-spy while a click-triggered smooth scroll runs. */
  private isProgrammaticScroll = false;
  private scrollSettleTimer?: ReturnType<typeof setTimeout>;

  /** Accumulates wheel delta over the sidebar until it reaches one "step". */
  private sidebarWheelAccum = 0;

  detailPane?: ElementRef<HTMLElement>;
  sidebar?: ElementRef<HTMLElement>;

  /*
   * The pane's scroll and the sidebar's wheel fire many times a second, and the
   * app runs zone change detection with no OnPush, so a template binding would
   * check the whole page on every event. They are attached outside the zone
   * instead, re-entering it only when the selection actually changes. Both
   * elements come and go with their @if branches, so the listeners follow the
   * view queries.
   */
  private readonly paneScrollListener = () => this.onDetailScroll();
  private readonly sidebarWheelListener = (event: WheelEvent) =>
    this.onSidebarWheel(event);

  @ViewChild('detailPane') set detailPaneQuery(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    if (ref?.nativeElement === this.detailPane?.nativeElement) return;
    this.detailPane?.nativeElement.removeEventListener(
      'scroll',
      this.paneScrollListener,
    );
    this.detailPane = ref;
    this.zone.runOutsideAngular(() =>
      ref?.nativeElement.addEventListener('scroll', this.paneScrollListener),
    );
  }

  @ViewChild('sidebar') set sidebarQuery(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    if (ref?.nativeElement === this.sidebar?.nativeElement) return;
    this.sidebar?.nativeElement.removeEventListener(
      'wheel',
      this.sidebarWheelListener,
    );
    this.sidebar = ref;
    // Dialog only: inline there is a page around the card, and cancelling
    // every wheel event over the sidebar would trap it, so there the sidebar
    // scrolls natively and hands the wheel on to the page at its ends. Not
    // passive, because the dialog's stepping cancels the native scroll.
    if (!this.isDialogMode) return;
    this.zone.runOutsideAngular(() =>
      ref?.nativeElement.addEventListener('wheel', this.sidebarWheelListener, {
        passive: false,
      }),
    );
  }
  @ViewChildren('noteSection') noteSections?: QueryList<
    ElementRef<HTMLElement>
  >;
  @ViewChildren('sidebarItem') sidebarItems?: QueryList<
    ElementRef<HTMLElement>
  >;

  get sidebarLabel(): string {
    return this.mode === 'matter' ? 'Resources' : 'Matters';
  }

  /**
   * The sidebar is a navigator; on the viewer's own view with a single note
   * there is nothing to navigate, and every part of it restates what the tab
   * already says — a "Resources (1)" heading over one entry bearing the
   * viewer's own name, beside a note header repeating it. Same reasoning as
   * showViewToggle, applied to a different control.
   *
   * Scoped to a single row on purpose: resource mode lists one of the viewer's
   * notes per matter, and there the sidebar lists MATTERS, which is the only
   * thing telling those notes apart.
   *
   * Note this also governs the help icon, which lives in the sidebar header.
   * That costs nothing today (viewHelpTooltip is null on the mine view), but
   * adding help text there later would need the icon rehoused.
   */
  get showSidebar(): boolean {
    return !(this.activeView === 'mine' && this.rows.length === 1);
  }

  /** Help text for the active view, or null where none is warranted. */
  get viewHelpTooltip(): string | null {
    return VIEW_HELP_TOOLTIPS[this.activeView];
  }

  ngOnInit(): void {
    // Ownership cannot be settled once and stored: user state loads
    // asynchronously after bootstrap (bootstrap$ is fire-and-forget and no
    // route guard awaits it), so on a cold deep-link the assignments — fetched
    // here in a dialog, fed by the host inline — can land before the user
    // record. Re-splitting on every emission is what stops the viewer's own
    // note being filed under "Resource Notes" for the life of the panel.
    // Subscribed before any rows exist (ahead of the fetch in a dialog), so the
    // BehaviorSubject's replay lands while the lists are still empty.
    this.authService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const previousKey = this.selectedRowKey();
        if (!this.applyOwnershipSplit()) return;
        this.applyViewAfterRowsChange(previousKey, 'user-split');
      });

    // Inline, the host supplies the list through the assignments input.
    if (!this.isDialogMode) return;

    if (this.mode === 'matter' && this.clientMatterNumber) {
      this.assignmentService
        .getAssignmentsByMatter(this.clientMatterNumber)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (assignments) => this.setRows(assignments),
          error: () => this.setError(),
        });
    } else if (this.mode === 'resource' && this.employeeId) {
      this.assignmentService
        .getAssignmentsByEmployee(this.employeeId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (assignments) => this.setRows(assignments),
          error: () => this.setError(),
        });
    } else {
      this.setError();
    }
  }

  ngOnDestroy(): void {
    // The scroll-settle timer can be armed by a click or wheel step that
    // happens just before the panel goes away (its dialog closes, or its page
    // is left); cancel it so its callback never fires against a torn-down view.
    clearTimeout(this.scrollSettleTimer);
    this.detailPane?.nativeElement.removeEventListener(
      'scroll',
      this.paneScrollListener,
    );
    this.sidebar?.nativeElement.removeEventListener(
      'wheel',
      this.sidebarWheelListener,
    );
  }

  selectNote(index: number): void {
    this.selectedIndex = index;
    this.isProgrammaticScroll = true;
    this.restartScrollSettleTimer();
    this.scrollPaneToNote(index, 'smooth');
    this.keepSidebarItemVisible(index);
  }

  /**
   * Scrolls the detail pane so the note sits at its top, honouring the
   * section's scroll-margin-top as scrollIntoView({ block: 'start' }) would.
   * Done on the pane's own scroll position because scrollIntoView also scrolls
   * every scrollable ancestor: inline, it would move the page to the card.
   */
  private scrollPaneToNote(index: number, behavior: ScrollBehavior): void {
    const pane = this.detailPane?.nativeElement;
    const section = this.noteSections?.get(index)?.nativeElement;
    if (!pane || !section) return;
    const margin = parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
    pane.scrollTo({
      top:
        pane.scrollTop +
        section.getBoundingClientRect().top -
        pane.getBoundingClientRect().top -
        margin,
      behavior,
    });
  }

  /**
   * Scrolling over the sidebar steps the selection (and the notes panel)
   * instead of scrolling the sidebar itself — the counterpart of the
   * notes-panel scroll-spy. Attached in a dialog only, outside the zone (see
   * sidebarQuery).
   */
  onSidebarWheel(event: WheelEvent): void {
    event.preventDefault();
    this.sidebarWheelAccum += event.deltaY;
    // One wheel notch is ~100px; step once per threshold and swallow the
    // remainder so trackpad inertia doesn't skip several names at once.
    if (Math.abs(this.sidebarWheelAccum) < 80) return;
    const direction = this.sidebarWheelAccum > 0 ? 1 : -1;
    this.sidebarWheelAccum = 0;
    const next = Math.min(
      Math.max(this.selectedIndex + direction, 0),
      this.rows.length - 1,
    );
    if (next !== this.selectedIndex) {
      this.zone.run(() => this.selectNote(next));
    }
  }

  /** The scroll-spy. Runs outside the zone (see detailPaneQuery). */
  onDetailScroll(): void {
    if (this.isProgrammaticScroll) {
      // Smooth scrolling fires many scroll events; keep pushing the settle
      // timeout back until they stop, then re-enable the spy.
      this.restartScrollSettleTimer();
      return;
    }

    const pane = this.detailPane?.nativeElement;
    const sections = this.noteSections;
    if (!pane || !sections?.length) return;

    // A section is "active" once it crosses the reading line a third of the
    // way down the pane; at the very bottom, force the last section so it can
    // become active even when too short to reach the reading line.
    let active: number;
    if (pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 1) {
      active = sections.length - 1;
    } else {
      // Compare each section's position to the reading line using
      // viewport-relative rects, so tracking doesn't depend on the sections'
      // offsetParent (which a future `position` change on an ancestor could
      // otherwise silently alter).
      const paneTop = pane.getBoundingClientRect().top;
      const readingLine = pane.clientHeight / 3;
      active = 0;
      sections.forEach((section, i) => {
        const sectionTop =
          section.nativeElement.getBoundingClientRect().top - paneTop;
        if (sectionTop <= readingLine) active = i;
      });
    }
    if (active !== this.selectedIndex) {
      this.zone.run(() => {
        this.selectedIndex = active;
        this.keepSidebarItemVisible(active);
      });
    }
  }

  /**
   * Brings the selected note to the top of the detail pane once the view has
   * rendered, for a re-feed that kept the reader's note but not its position:
   * the panes were rebuilt by a reload, or entries were added or removed above
   * it. Scrolled within the panes only (see scrollPaneToNote), and instantly:
   * the reader did not ask for a move, so there is nothing to animate.
   */
  private scrollToSelectionAfterRender(): void {
    afterNextRender(
      () => {
        this.isProgrammaticScroll = true;
        this.restartScrollSettleTimer();
        this.scrollPaneToNote(this.selectedIndex, 'instant');
        this.keepSidebarItemVisible(this.selectedIndex);
      },
      { injector: this.injector },
    );
  }

  /** The selected note's assignment, as the key to find it again by. */
  private selectedRowKey(): number | undefined {
    return this.rows[this.selectedIndex]?.assignmentId;
  }

  /**
   * Keeps the selected item in view when the sidebar itself overflows, scrolling
   * the nearest distance as scrollIntoView({ block: 'nearest' }) would. Done on
   * the sidebar's own scrollTop because scrollIntoView also scrolls every
   * scrollable ancestor: inline, the scroll-spy would move the page under the
   * reader as they scroll the notes.
   */
  private keepSidebarItemVisible(index: number): void {
    const sidebar = this.sidebar?.nativeElement;
    const item = this.sidebarItems?.get(index)?.nativeElement;
    if (!sidebar || !item) return;
    const sidebarRect = sidebar.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    // The sticky "Resources (N)" header covers the top of the sidebar, so an
    // item is only visible below it; measured rather than assumed, since the
    // header's height follows its font and padding tokens.
    const header = sidebar.querySelector<HTMLElement>('.sidebar-header');
    const visibleTop = sidebarRect.top + (header?.offsetHeight ?? 0);
    if (itemRect.bottom > sidebarRect.bottom) {
      sidebar.scrollTop += itemRect.bottom - sidebarRect.bottom;
    } else if (itemRect.top < visibleTop) {
      sidebar.scrollTop -= visibleTop - itemRect.top;
    }
  }

  private restartScrollSettleTimer(): void {
    clearTimeout(this.scrollSettleTimer);
    this.scrollSettleTimer = setTimeout(() => {
      this.isProgrammaticScroll = false;
    }, 150);
  }

  /**
   * Opens this same panel in a floating dialog. Only the identifiers are
   * passed, not the host's list: the dialog fetches its own so it keeps
   * working after the page that opened it is gone. The key focuses an
   * already-open dialog for the same entity instead of stacking a second.
   */
  openInFloatingDialog(): void {
    const id =
      this.mode === 'matter' ? this.clientMatterNumber : this.employeeId;
    if (!id) return;
    this.floatingDialogService.open(
      AssignmentNotesPanelComponent,
      // || rather than ??, so an empty name falls back to the identifier too.
      `Assignment Notes — ${this.entityName() || id}`,
      {
        mode: this.mode,
        clientMatterNumber: this.clientMatterNumber,
        employeeId: this.employeeId,
      },
      {
        centered: true,
        heightRatio: 5 / 6,
        widthRatio: 7 / 6,
        key: `all-notes:${this.mode}:${id}`,
      },
    );
  }

  private setRows(source: AssignmentNotesSource[]): void {
    const previousKey = this.selectedRowKey();
    // A row with no assignment id is a synthetic latest-billed one, which the
    // API never gives notes. Dropped here rather than trusted, since an
    // id-less row would collide on the track key and the selection key.
    const assignments = source.filter(
      (a): a is AssignmentNotesSource & { assignedResourceId: number } =>
        a.assignedResourceId != null,
    );

    this.managerNoteRows = assignments
      .filter((a) => a.hasManagerNote)
      .map((a) => {
        // Only the manager note has a restricted state: its existence flag
        // survives a visibility miss so it can be listed without its content.
        const locked = a.canViewResource === false;
        return {
          ...this.toIdentityFields(a),
          assignmentId: a.assignedResourceId,
          locked,
          noteHtml: locked ? '' : this.toNoteHtml(a.managerNotesInfo),
          ...this.toBylineFields(locked ? undefined : a.managerNotesInfo),
        };
      });

    this.allResourceNoteRows = assignments
      .filter((a) => a.hasResourceNote)
      .map((a) => ({
        ...this.toIdentityFields(a),
        assignmentId: a.assignedResourceId,
        // hasResourceNote is only ever true when the note is readable, so there
        // is no locked state to represent here.
        locked: false,
        noteHtml: this.toNoteHtml(a.resourceNotesInfo),
        ...this.toBylineFields(a.resourceNotesInfo),
        // Resource mode fetches one employee's assignments, so the DTO carries
        // no employeeId of its own — the panel's input identifies the owner.
        employeeId: 'employeeId' in a ? a.employeeId : this.employeeId,
      }));

    this.applyOwnershipSplit();
    this.applyViewAfterRowsChange(previousKey, 'feed');
    this.error = null;
    this.isLoading = false;
  }

  /**
   * Settles the view and selection after the rows change, either because they
   * were (re)built from a list ('feed': the first load, or an inline host's
   * re-feed after a note edit or a reload) or because a late user record
   * re-split them ('user-split'). A re-feed must not throw the reader back to
   * the default tab or move them off the note they were reading:
   * - a feed keeps the current view while it still has notes, chosen or not.
   *   On the first feed the view is still its initial 'mine', the top of the
   *   default priority, so that is the default priority anyway. A user split
   *   moves notes between views, so it keeps only a view the reader picked;
   * - otherwise, or once the view empties, the default priority picks one;
   * - on the same view, the selection follows the note (by its assignment),
   *   falling back to the same position, clamped, when that note is gone; the
   *   pane is then scrolled to it if the panes were rebuilt or the note moved;
   * - a different view starts at its first note, with both panes at the top.
   */
  private applyViewAfterRowsChange(
    previousKey: number | undefined,
    change: 'feed' | 'user-split',
  ): void {
    const keepCurrentView = change === 'feed' || this.viewChosenByUser;
    // The loading and error branches replace the panes, so a feed arriving in
    // either brings the panes back at the top whatever the reader had scrolled
    // to. Read before setRows clears both.
    const panesRebuilt =
      change === 'feed' && (this.isLoading || this.error !== null);
    const previousView = this.activeView;
    const previousIndex = this.selectedIndex;
    const keepView =
      keepCurrentView && this.rowsFor(previousView).length > 0;
    if (!keepView) this.activeView = this.defaultView();
    if (this.activeView !== previousView) {
      this.selectedIndex = 0;
      this.resetPanesToTop();
      return;
    }

    const keyedIndex =
      previousKey === undefined
        ? -1
        : this.rows.findIndex((r) => r.assignmentId === previousKey);
    this.selectedIndex =
      keyedIndex >= 0
        ? keyedIndex
        : Math.min(previousIndex, Math.max(this.rows.length - 1, 0));
    const paneOffSelection = panesRebuilt
      ? this.selectedIndex > 0
      : this.selectedIndex !== previousIndex;
    if (paneOffSelection) this.scrollToSelectionAfterRender();
  }

  /**
   * Re-derives ownership and the my/resource split from the current user.
   *
   * Ownership is asked of AuthService at every pass rather than stored on the
   * row, because isCurrentEmployee answers false while the user record is still
   * loading and a stored answer from that window would never correct itself.
   *
   * Returns whether anything actually moved, so a no-op emission does not
   * disturb the reader's scroll position.
   */
  private applyOwnershipSplit(): boolean {
    let changed = false;
    for (const row of this.allResourceNoteRows) {
      const isOwnNote = this.authService.isCurrentEmployee(row.employeeId);
      if (isOwnNote !== row.isOwnNote) {
        row.isOwnNote = isOwnNote;
        changed = true;
      }
    }

    // The viewer's own notes get their own view; everyone else's are only here
    // at all when the viewer holds Assignments.ResourceNotes.Read. Assigned
    // unconditionally so the two lists can never survive a reload of the rows.
    this.myNoteRows = this.allResourceNoteRows.filter((r) => r.isOwnNote);
    this.resourceNoteRows = this.allResourceNoteRows.filter((r) => !r.isOwnNote);

    // The single point where any of the three lists can change, so the only
    // place the view list needs rebuilding. Manager rows are already assigned
    // by the time setRows calls this.
    this.availableViews = this.buildAvailableViews();
    return changed;
  }

  /**
   * Opens on the viewer's own note, then other resources', then manager notes —
   * never on an empty pane while another view has content. Applied whenever the
   * rows change, unless the viewer has picked a view that still has notes (see
   * applyViewAfterRowsChange).
   */
  private defaultView(): AssignmentNotesView {
    return this.myNoteRows.length > 0
      ? 'mine'
      : this.resourceNoteRows.length > 0
        ? 'resource'
        : 'manager';
  }

  /** The name/matter columns shared by both views. */
  private toIdentityFields(a: AssignmentNotesSource): Partial<NoteRow> {
    return {
      fullName: 'fullName' in a ? a.fullName : undefined,
      title: 'title' in a ? a.title : undefined,
      classYear: 'classYear' in a ? a.classYear : undefined,
      seniority: 'seniority' in a ? a.seniority : undefined,
      clientMatterNumber:
        'clientMatterNumber' in a ? a.clientMatterNumber : undefined,
      clientName: 'clientName' in a ? a.clientName : undefined,
      matterTitle: 'matterTitle' in a ? a.matterTitle : undefined,
      // Declared by both DTOs, so no narrowing needed.
      assignmentTypeCategoryName: a.assignmentTypeCategoryName,
    };
  }

  private toBylineFields(
    notesInfo: NotesWithMentionsDto | null | undefined,
  ): Partial<NoteRow> {
    const updatedBy = notesInfo?.lastUpdatedBy;
    return {
      lastUpdatedName: formatUserEditHistoryName(updatedBy),
      lastUpdatedTimestamp: updatedBy?.timestamp,
    };
  }

  private setError(): void {
    this.error = 'Failed to load assignment notes.';
    this.isLoading = false;
  }

  /**
   * The displayed body for a note: escaped or passed through by toDisplayHtml,
   * then with its mention tokens resolved to links.
   *
   * Mentions are resolved AFTER escaping, not before — the other order would
   * escape the anchors this generates and print them as literal markup. Both
   * note slots carry mentions (the API parses them in
   * RequestService.BuildNotesWithMentionsAsync), so both go through here.
   */
  private toNoteHtml(notesInfo: NotesWithMentionsDto | null | undefined): string {
    return resolveMentionLinks(
      this.toDisplayHtml(notesInfo?.notes ?? ''),
      notesInfo?.mentions,
    );
  }

  /**
   * Tags the rich-text entity-notes editor actually emits (contenteditable +
   * execCommand output, plus mention anchors). Only a note containing one of
   * these is treated as pre-formatted HTML.
   */
  private static readonly EDITOR_HTML_TAG =
    /<\/?(a|b|br|div|em|i|li|ol|p|span|strong|u|ul)(\s[^<>]*)?\/?>/i;

  /**
   * Notes come from two editors: the rich-text entity-notes editor (HTML)
   * and the assignment edit dialog's plain textarea, whose line breaks are
   * \n characters that innerHTML would collapse into spaces.
   *
   * A note is only treated as pre-formatted HTML when it contains a tag the
   * rich-text editor actually emits (see EDITOR_HTML_TAG). Everything else is
   * treated as plain text: its `<`, `>` and `&` are escaped so tokens like
   * "List<Claim>", "<redacted>", or "<TBD>" render literally instead of being
   * stripped by Angular's sanitizer as unknown tags, and its line breaks are
   * converted to <br> so paragraph breaks survive.
   */
  private toDisplayHtml(notes: string): string {
    if (!notes) {
      return notes;
    }
    if (AssignmentNotesPanelComponent.EDITOR_HTML_TAG.test(notes)) {
      return notes;
    }
    return notes
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\r?\n/g, '<br>');
  }
}
